import { getFunctions, httpsCallable } from "firebase/functions";
import { isExplicitCallExitSource } from "./callNavigationGuard";
import {
  createShutdownId,
  shutdownAuditDone,
  shutdownAuditStart,
  shutdownAuditStep,
} from "./shutdownAudit";
import { markWebCallShutdownRequested } from "./webCallMediaRegistry";

export const CALL_LIFECYCLE_STATES = [
  "preparing",
  "ringing",
  "accepted",
  "loading_token",
  "web_zego_starting",
  "zego_login",
  "media_starting",
  "active",
  "ending",
  "ended",
  "failed",
];

const lifecycleByCallId = new Map();
const endCallInvokedByCallId = new Set();
const exitTriggeredByCallId = new Set();
const shutdownInProgressByCallId = new Set();
const perfOriginByCallId = new Map();

let shutdownHooks = {
  stopWebZego: async () => {},
  unsubscribeCallDoc: async () => {},
  navigateAway: () => {},
  shouldNavigateAway: () => true,
  getWebCleanupState: () => ({}),
};

export function registerShutdownHooks(hooks = {}) {
  shutdownHooks = {
    stopWebZego: hooks.stopWebZego || shutdownHooks.stopWebZego,
    unsubscribeCallDoc: hooks.unsubscribeCallDoc || shutdownHooks.unsubscribeCallDoc,
    navigateAway: hooks.navigateAway || shutdownHooks.navigateAway,
    shouldNavigateAway: hooks.shouldNavigateAway || shutdownHooks.shouldNavigateAway,
    getWebCleanupState: hooks.getWebCleanupState || shutdownHooks.getWebCleanupState,
  };
}

export function isTerminalCallStatus(data) {
  if (!data) return false;
  const status = String(data.status || "").toLowerCase();
  if (
    ["ended", "cancelled", "canceled", "rejected", "failed", "missed", "timeout"].includes(
      status
    )
  ) {
    return true;
  }
  return !!(data.endedAt || data.endedReason);
}

function getLifecycleRecord(callId) {
  if (!callId) return null;
  if (!lifecycleByCallId.has(callId)) {
    lifecycleByCallId.set(callId, {
      state: "preparing",
      role: null,
      platformMode: "unknown",
      startedAt: performance.now(),
      updatedAt: performance.now(),
    });
  }
  return lifecycleByCallId.get(callId);
}

export function resetCallLifecycle(callId, { role = null, platformMode = "unknown" } = {}) {
  if (!callId) return;
  lifecycleByCallId.set(callId, {
    state: "preparing",
    role,
    platformMode,
    startedAt: performance.now(),
    updatedAt: performance.now(),
  });
  endCallInvokedByCallId.delete(callId);
  exitTriggeredByCallId.delete(callId);
  perfOriginByCallId.set(callId, performance.now());
  transitionCallLifecycle(callId, "preparing", { role, platformMode, reason: "reset" });
}

export function transitionCallLifecycle(callId, state, extra = {}) {
  if (!callId || !CALL_LIFECYCLE_STATES.includes(state)) return;
  const record = getLifecycleRecord(callId);
  if (!record) return;

  if (extra.role) record.role = extra.role;
  if (extra.platformMode) record.platformMode = extra.platformMode;
  record.state = state;
  record.updatedAt = performance.now();

  const elapsedMs = Math.round(record.updatedAt - record.startedAt);
  console.log(`[callLifecycle] state ${state}`, {
    callId,
    role: record.role,
    platformMode: record.platformMode,
    elapsedMs,
    ...extra,
  });
}

export function markCallPerf(callId, label, extra = {}) {
  const t0 = perfOriginByCallId.get(callId) || performance.now();
  const elapsed = Math.round(performance.now() - t0);
  console.log(`[callPerf] ${label}`, {
    callId,
    elapsed,
    ...extra,
  });
  if (label === "call_active") {
    console.log(`[callPerf] call_active elapsed_total=${elapsed}`, { callId, ...extra });
  }
}

export async function requestEndCallCallable(callId, source, endCallReason = null, shutdownId = null) {
  if (!callId) {
    return { ok: false, error: "missing_callId" };
  }

  const traceId = shutdownId || createShutdownId(callId, source);
  shutdownAuditStep(traceId, "firebase_endCall_invoke", true, { callId, source, endCallReason });

  console.log("[callLifecycle] endCall request", { source, callId, reason: endCallReason });

  if (endCallInvokedByCallId.has(callId)) {
    console.log("[callLifecycle] endCall skipped duplicate", { callId, source });
    shutdownAuditStep(traceId, "firebase_endCall_skipped_duplicate", true, { callId, source });
    return { ok: false, duplicate: true };
  }

  endCallInvokedByCallId.add(callId);
  transitionCallLifecycle(callId, "ending", { reason: endCallReason || source });

  console.log(`[hangupSync] invoking endCall callId=${callId} source=${source}`);
  console.log("[callLifecycle] endCall invoke callable", { callId, source });

  try {
    const functions = getFunctions(undefined, "us-central1");
    const endCallFn = httpsCallable(functions, "endCall");
    const payload = { callId };
    if (endCallReason) payload.reason = endCallReason;

    await endCallFn(payload);
    console.log(
      `[hangupSync] endCall success callId=${callId} status should become ended source=${source}`
    );
    console.log("[callLifecycle] endCall success", { callId, source });
    console.log("[callBusy] release expected after endCall success", { callId, source });
    shutdownAuditStep(traceId, "firebase_endCall_success", true, { callId, source });
    transitionCallLifecycle(callId, "ended", { reason: endCallReason || source });
    return { ok: true };
  } catch (e) {
    endCallInvokedByCallId.delete(callId);
    console.error("[callLifecycle] endCall failed", {
      callId,
      source,
      error: e?.message || e,
    });
    shutdownAuditStep(traceId, "firebase_endCall_fail", false, {
      callId,
      source,
      error: e?.message || e,
    });
    return { ok: false, error: e };
  }
}

export function hasExitTriggered(callId) {
  return exitTriggeredByCallId.has(callId);
}

export function isShutdownInProgress(callId) {
  return shutdownInProgressByCallId.has(callId);
}

export function markExitTriggered(callId) {
  if (callId) exitTriggeredByCallId.add(callId);
}

export function clearCallLifecycleGuards(callId) {
  if (!callId) return;
  endCallInvokedByCallId.delete(callId);
  exitTriggeredByCallId.delete(callId);
  shutdownInProgressByCallId.delete(callId);
  lifecycleByCallId.delete(callId);
  perfOriginByCallId.delete(callId);
}

/**
 * Authoritative call shutdown: Firebase endCall (when required), stop Web Zego,
 * tear down listeners, optional navigate.
 */
export async function requestEndCall({
  callId,
  source = "unknown",
  userInitiated = false,
  navigateAfter = true,
  endCallReason = null,
  invokeEndCall = null,
  failureMustEnd = false,
  shutdownId = null,
} = {}) {
  if (!callId) {
    return { ok: false, error: "missing_callId" };
  }

  markExitTriggered(callId);

  markWebCallShutdownRequested(callId, source, {
    exitTriggered: true,
    shutdownInProgress: shutdownInProgressByCallId.has(callId),
  });

  const traceId = shutdownId || createShutdownId(callId, source);

  if (shutdownInProgressByCallId.has(callId)) {
    console.log("[callLifecycle] shutdown skipped duplicate", { callId, source });
    shutdownAuditStep(traceId, "shutdown_skipped_duplicate", true, { callId, source });
    if (isExplicitCallExitSource(source)) {
      const allowNavigate =
        shutdownHooks.shouldNavigateAway?.(callId, source, traceId) !== false;
      if (allowNavigate) {
        try {
          shutdownHooks.navigateAway(callId, source, traceId);
        } catch (e) {
          console.warn("[hangupSync] duplicate shutdown navigate retry failed", {
            callId,
            source,
            error: e?.message || e,
          });
        }
      }
    }
    return { ok: false, duplicate: true };
  }

  shutdownInProgressByCallId.add(callId);
  shutdownAuditStart(traceId, source, "web");
  shutdownAuditStep(traceId, "requestEndCall_called", true, {
    userInitiated,
    navigateAfter,
    invokeEndCall,
  });

  console.log("[callLifecycle] shutdown_start", {
    callId,
    source,
    userInitiated,
    navigateAfter,
    shutdownId: traceId,
  });

  const shouldInvokeEndCall =
    invokeEndCall !== null && invokeEndCall !== undefined
      ? !!invokeEndCall
      : userInitiated || failureMustEnd;

  let endCallResult = { ok: false, skipped: true };
  if (shouldInvokeEndCall) {
    const reason =
      endCallReason ||
      (userInitiated ? "hangup" : failureMustEnd ? "call_failure" : "hangup");
    endCallResult = await requestEndCallCallable(callId, source, reason, traceId);
  } else {
    shutdownAuditStep(traceId, "firebase_endCall_skipped_terminal", true, { source });
    transitionCallLifecycle(callId, "ending", { reason: source });
    transitionCallLifecycle(callId, "ended", { reason: source });
  }

  shutdownAuditStep(traceId, "stop_web_zego_start", true, { callId, source });
  console.log("[callLifecycle] stop web zego start", { callId, source });
  try {
    await shutdownHooks.stopWebZego(callId, source, traceId);
    shutdownAuditStep(traceId, "stop_web_zego_done", true, { callId, source });
  } catch (e) {
    shutdownAuditStep(traceId, "stop_web_zego_done", false, {
      callId,
      source,
      error: e?.message || e,
    });
    console.warn("[callLifecycle] stop web zego failed", {
      callId,
      source,
      error: e?.message || e,
    });
  }
  console.log("[callLifecycle] stop web zego done", { callId, source });

  try {
    await shutdownHooks.unsubscribeCallDoc(callId, traceId);
    shutdownAuditStep(traceId, "call_onSnapshot_unsubscribed", true, { callId });
  } catch (e) {
    shutdownAuditStep(traceId, "call_onSnapshot_unsubscribed", false, {
      callId,
      error: e?.message || e,
    });
  }

  console.log("[callLifecycle] unsubscribe call listeners done", { callId });

  if (navigateAfter) {
    const allowNavigate = shutdownHooks.shouldNavigateAway?.(callId, source, traceId) !== false;
    if (!allowNavigate) {
      console.log("[callNavigationGuard] blocked shutdown navigation", {
        callId,
        source,
        shutdownId: traceId,
      });
      shutdownAuditStep(traceId, "navigation_blocked_active_call", true, { callId, source });
    } else {
      try {
        shutdownHooks.navigateAway(callId, source, traceId);
        shutdownAuditStep(traceId, "navigation_executed", true, { callId, source });
      } catch (e) {
        shutdownAuditStep(traceId, "navigation_executed", false, {
          callId,
          error: e?.message || e,
        });
        console.warn("[callLifecycle] navigateAway failed", {
          callId,
          error: e?.message || e,
        });
      }
    }
  }

  const remainingState = shutdownHooks.getWebCleanupState?.() || {};
  if (
    remainingState.heartbeatCleared === false ||
    remainingState.timerCleared === false ||
    remainingState.zegoEngineNull === false ||
    remainingState.remoteElementsCleared === false
  ) {
    console.error("[shutdownAudit] ERROR active call state remains after shutdown", {
      shutdownId: traceId,
      ...remainingState,
    });
  }

  lifecycleByCallId.delete(callId);
  perfOriginByCallId.delete(callId);
  shutdownInProgressByCallId.delete(callId);

  console.log("[callLifecycle] shutdown_done", { callId, source, shutdownId: traceId });
  shutdownAuditDone(traceId, "web");
  return { ok: true, endCallResult, shutdownId: traceId };
}
